import { BulkCreateProviderTerminationsModel, BulkTerminationProjectResponseModel, BulkProjectTypeEnum, ImportBulkTerminationFileResponse } from "@app/terminations/import/interfaces";
import { BulkProviderChangeReportResponseModel } from "@app/shared/provider-change-wizards/interfaces";
import { LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType, ProjectRunConfigurationType } from "@app/projects/enumerators";
import { NetworkServicingProviderTerminationModel } from "@app/provider-terminations/networkServicingProviderTerminationModel";
import UpdateProviderTermination from "@app/provider-terminations/updateProviderTermination";
import { SortDirections } from "@app/shared/waffles";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { CareJourneyScoresValueEnum, EffectivenessEfficiencyValueEnum, MotiveScoreValueEnum, NewPatientClaimsEnum } from "@app/projects/servicing-provider";
import { SpecialtyPredictionModelEnum } from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelEnum";
import { ProviderToContractEntityModel } from "@app/projects/providerToContractEntityModel";
import { SpecialtyGroupCodeToProviderIdsModel } from "@app/projects/specialtyGroupCodeToProviderIdsModel";
import ServiceBase from "@app/shared/serviceBase";

/** Manages access to CRUD functionality for provider sets. */
export default class ProviderTerminationService extends ServiceBase {
  public async getTerminatedProviders(
    projectId: number,
    projectRunConfigurationId: number,
    regionSetId: number,
    specialtyGroupCode: string,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    sortedColumn: string,
    sortDirection: SortDirections,
    columnSubKey: string,
    searchValue?: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
    selectedTopProcedures?: string[],
    selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[],
    selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[],
    selectedTopConditions?: string[],
    specialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    selectedPgrConfidenceTiers?: number[],
  ): Promise<NetworkServicingProviderTerminationModel[]> {
    if (!projectId || !projectRunConfigurationId || !regionSetId || !specialtyGroupCode || !networkAssessmentAnalysisType) {
      return null;
    }

    if (!selectedVolumeLabels) {
      selectedVolumeLabels = [];
    }

    return await this.makePost(
      `providerTerminations/${projectId}/runConfigurations/${projectRunConfigurationId}/regionsets/${regionSetId}`,
      {
        columnSubKey,
        networkAssessmentAnalysisType,
        searchValue,
        selectedCareJourneyCostValues,
        selectedCareJourneyQualityValues,
        selectedEffectivenessValues,
        selectedEfficiencyValues,
        selectedLineOfBusiness,
        selectedLocationConfidenceValues,
        selectedMotiveAppropriatenessValues,
        selectedNewPatientClaimsValues,
        selectedPgrConfidenceTiers,
        selectedTopConditions,
        selectedTopProcedures,
        selectedVolumeLabels,
        sortDirection,
        sortedColumn,
        specialtyGroupCode,
        specialtyPredictionModelFilter,
      });
  }

  public async createProviderTerminations(
    customProjectId: number,
    terminateByNPI: boolean,
    terminateByTIN: boolean,
    terminateByContractEntity: boolean,
    terminateByNPI2: boolean,
    providerIds: number[],
    reasonName: string,
    reasonDescription: string,
    allSpecialties: boolean,
    allLocations: boolean,
    specialtyGroupCode: string,
    providerIdToContractEntityId: ProviderToContractEntityModel[],
    terminateByCCN = false,
    ccn = ""
  ): Promise<string> {
    return await this.makePost(`providerTerminations/create`, { allLocations, allSpecialties, ccn, customProjectId, providerIds, providerIdToContractEntityId, reasonDescription, reasonName, specialtyGroupCode, terminateByCCN, terminateByContractEntity, terminateByNPI, terminateByNPI2, terminateByTIN });
  }

  public async createGhostProviderTerminations(
    customProjectId: number,
    terminateByNPI: boolean,
    reasonName: string,
    reasonDescription: string,
    npis: string[],
  ): Promise<string> {
    return await this.makePost(`providerTerminations/createGhostProviderTerminations`, { customProjectId, npis, reasonDescription, reasonName, terminateByNPI })
  }

  public async createNetworkIntegrityProviderTerminations(
    customProjectId: number,
    reasonDescription: string,
    allSpecialties: boolean,
    allLocations: boolean,
    specialtyGroupCodeToProviderIds: SpecialtyGroupCodeToProviderIdsModel[],
    projectRunConfigurationType: ProjectRunConfigurationType,
    terminateByNPI: boolean,
    reasonName: string
  ): Promise<string> {
    return await this.makePost(`providerTerminations/create-network-integrity-provider-terminations?allowDuplicates=true&dualMembershipProjectRunConfigurationType=${projectRunConfigurationType}`, { allLocations,
                                                                                                        allSpecialties,
                                                                                                        customProjectId,
                                                                                                        reasonDescription,
                                                                                                        reasonName,
                                                                                                        specialtyGroupCodeToProviderIds,
                                                                                                        terminateByNPI });
  }

  public async getProviderTerminationReasons(): Promise<string[]> {
    return await this.makeGet(`providerTerminations/providerterminationreasons`);
  }

  public async deleteTermination(projectId: number, terminationIds: number[]): Promise<void> {
    return await this.makeDelete(`providerTerminations/${projectId}`, terminationIds);
  }

  public async restoreTermination(projectId: number, terminationIds: number[]): Promise<void> {
    return await this.makePost(`providerTerminations/${projectId}/restoreterminations`, terminationIds);
  }

  public async getProviderTermination(projectId: number, terminationId: number): Promise<NetworkServicingProviderTerminationModel> {
    return await this.makePost<NetworkServicingProviderTerminationModel>(`providerTerminations/${projectId}/getprovidertermination`, terminationId);
  }

  public async getUnprocessedProviderTerminations(projectId: number): Promise<NetworkServicingProviderTerminationModel[]> {
    if (!projectId) {
      return null;
    }
    return await this.makeGet<NetworkServicingProviderTerminationModel[]>(`providerTerminations/${projectId}/getunprocessedproviderterminations`);
  }

  public async getProcessedProviderTerminations(projectId: number): Promise<NetworkServicingProviderTerminationModel[]> {
    if (!projectId) {
      return null;
    }

    return await this.makeGet<NetworkServicingProviderTerminationModel[]>(`providerTerminations/${projectId}/getprocessedproviderterminations`);
  }

  public async updateProviderTermination(projectId: number, updateProviderTermination: UpdateProviderTermination): Promise<void> {
    return await this.makePatch(`providerTerminations/${projectId}`, updateProviderTermination);
  }

  // Bulk Terminations
  public async getBulkTerminationProjects(filterToPciProjects: boolean = false, projectType: BulkProjectTypeEnum = BulkProjectTypeEnum.County): Promise<BulkTerminationProjectResponseModel[]> {
    // The endpoint also takes query params for includeShared, includeHidden, and resourceStatus, but we're good with the defaults
    return await this.makeGet<BulkTerminationProjectResponseModel[]>(`providerTerminations/bulkproviderterminationprojects`, { filterToPciProjects, projectType });
  }

  public async importBulkTerminationFile(bulkNpiTinFile: FormData, terminateByNpi: boolean, terminateByTin: boolean, terminateByAllLocations: boolean, terminateByAllSpecialties: boolean): Promise<ImportBulkTerminationFileResponse> {
    if (!bulkNpiTinFile) {
      return null;
    }

    const queryString = `?terminateByNpi=${terminateByNpi}&terminateByTin=${terminateByTin}${!terminateByAllLocations ? `&allLocations=${terminateByAllLocations}` : ''}${!terminateByAllSpecialties ? `&allSpecialties=${terminateByAllSpecialties}` : ''}`;
    return await this.makePost<ImportBulkTerminationFileResponse>(`providerTerminations/importbulkterminationfile${queryString}`, bulkNpiTinFile, undefined, true, false, false);
  }

  public async bulkCreateProviderTerminations(bulkCreateProviderTerminationsModel: BulkCreateProviderTerminationsModel): Promise<void> {
    return await this.makePost(`providerTerminations/bulkCreateProviderTerminations`, bulkCreateProviderTerminationsModel);
  }

  public async getBulkProviderTerminationExports(): Promise<BulkProviderChangeReportResponseModel[]> {
    return await this.makeGet<BulkProviderChangeReportResponseModel[]>("providerTerminations/bulkproviderterminationexports");
  }
}
